import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Typography,
  Chip,
  Box,
  IconButton,
  styled,
  CircularProgress,
  Divider,
  Avatar,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DevicesIcon from '@mui/icons-material/Devices';
import AssignmentIcon from '@mui/icons-material/Assignment';
import GroupIcon from '@mui/icons-material/Group';
import SyncIcon from '@mui/icons-material/Sync';
import InfoIcon from '@mui/icons-material/Info';
import { useDispatch } from 'react-redux';
import { storeDetailAssignment } from '../../../store/ratings/thunks';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 16,
    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(3),
  },
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
  '& .MuiTypography-root': {
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
}));

const InfoCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 12,
  backgroundColor: theme.palette.action.hover,
  border: `1px solid ${theme.palette.divider}`,
  height: '100%',
  transition: 'transform 0.2s',
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
  },
}));

interface Props {
  open: boolean;
  onClose: () => void;
  assignment: any;
}

export const DetailAssignmentModal = ({ open, onClose, assignment: initialAssignment }: Props) => {
  const dispatch = useDispatch();
  const [assignment, setAssignment] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && initialAssignment?.id) {
      fetchDetail(initialAssignment.id);
    } else if (!open) {
      setAssignment(null);
    }
  }, [open, initialAssignment?.id]);

  const fetchDetail = async (id: string) => {
    setLoading(true);
    const response = await dispatch(storeDetailAssignment(id, 'detalle'));
    if (response.ok) {
      setAssignment(response.data);
    }
    setLoading(false);
  };

  if (!open) return null;

  return (
    <BootstrapDialog onClose={onClose} open={open} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          m: 0,
          p: 2.5,
          fontWeight: 800,
          fontSize: '1.25rem',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          background: (theme) =>
            `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: '#fff',
        }}
      >
        <AssignmentIcon /> Detalle de Asignación
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 12,
          top: 12,
          color: '#fff',
          '&:hover': { background: 'rgba(255,255,255,0.1)' },
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent dividers sx={{ p: 0 }}>
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              p: 8,
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <CircularProgress size={48} thickness={4} />
            <Typography variant="body1" color="textSecondary" fontWeight="500">
              Obteniendo información premium...
            </Typography>
          </Box>
        ) : assignment ? (
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              {/* Sección Dispositivo y Estado */}
              <Grid size={{ xs: 12, md: 8 }}>
                <SectionHeader>
                  <DevicesIcon fontSize="small" />
                  <Typography>Hardware Vinculado</Typography>
                </SectionHeader>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Avatar sx={{ bgcolor: 'primary.light', width: 56, height: 56 }}>
                    <DevicesIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                      {assignment.deviceManagement?.name || 'Dispositivo Desconocido'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Código: <strong>{assignment.deviceManagement?.code || 'N/A'}</strong>
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <SectionHeader>
                  <InfoIcon fontSize="small" />
                  <Typography>Estado</Typography>
                </SectionHeader>
                <Box sx={{ mt: 1 }}>
                  {assignment.status === 1 ? (
                    <Chip
                      label="ACTIVO"
                      color="success"
                      sx={{ fontWeight: 800, px: 1, borderRadius: 2 }}
                    />
                  ) : (
                    <Chip
                      label="INACTIVO"
                      color="error"
                      sx={{ fontWeight: 800, px: 1, borderRadius: 2 }}
                    />
                  )}
                </Box>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              {/* Sección Encuestas */}
              <Grid size={{ xs: 12 }}>
                <SectionHeader>
                  <AssignmentIcon fontSize="small" />
                  <Typography>Encuestas Activas</Typography>
                </SectionHeader>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    p: 2,
                    bgcolor: 'rgba(25, 118, 210, 0.04)',
                    borderRadius: 3,
                    border: '1px dashed rgba(25, 118, 210, 0.3)',
                  }}
                >
                  {assignment.surveys &&
                  assignment.surveys.filter((s: any) => s.assigned !== false).length > 0 ? (
                    assignment.surveys
                      .filter((s: any) => s.assigned !== false)
                      .map((s: any) => (
                        <Chip
                          key={s.value}
                          label={s.label}
                          variant="filled"
                          color="primary"
                          size="medium"
                          sx={{ fontWeight: 600, borderRadius: 1.5 }}
                        />
                      ))
                  ) : (
                    <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                      No hay encuestas asignadas actualmente a este dispositivo.
                    </Typography>
                  )}
                </Box>
              </Grid>

              {/* Sección Usuarios */}
              {assignment.users && assignment.users.length > 0 && (
                <Grid size={{ xs: 12 }}>
                  <SectionHeader>
                    <GroupIcon fontSize="small" />
                    <Typography>Usuarios en Dispositivo</Typography>
                  </SectionHeader>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {assignment.users.map((u: any) => (
                      <Chip
                        key={u.value}
                        label={u.label}
                        size="small"
                        variant="outlined"
                        sx={{ borderStyle: 'dotted' }}
                      />
                    ))}
                  </Box>
                </Grid>
              )}

              {/* Sección Sync */}
              <Grid size={{ xs: 12 }}>
                <InfoCard sx={{ mt: 1, bgcolor: '#fafafa' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <SyncIcon color="action" />
                    <Box>
                      <Typography variant="caption" color="textSecondary" display="block">
                        ÚLTIMA SINCRONIZACIÓN
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {assignment.lastSyncAt
                          ? new Date(assignment.lastSyncAt).toLocaleString()
                          : 'Aún no se ha sincronizado'}
                      </Typography>
                    </Box>
                  </Box>
                </InfoCard>
              </Grid>
            </Grid>
          </Box>
        ) : (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="error">No se pudo cargar la información del servidor.</Typography>
          </Box>
        )}
      </DialogContent>
    </BootstrapDialog>
  );
};
